import { trackChristmasMarketCheckoutStarted, trackChristmasMarketPriceCalculated, trackChristmasMarketPricingView } from '~/modules/ChristmasMarketAnalytics.js'

function setHidden(node, hidden) {
  if (node) {
    node.hidden = hidden
  }
}

function setText(node, text) {
  if (node) {
    node.textContent = text
  }
}

function setFieldError(input, errorNode, message) {
  const hasError = Boolean(message)

  input.setAttribute('aria-invalid', hasError ? 'true' : 'false')
  if (hasError) {
    input.setAttribute('aria-errormessage', errorNode.id)
  } else {
    input.removeAttribute('aria-errormessage')
  }
  setText(errorNode, message || '')
  setHidden(errorNode, !hasError)
}

function setCheckoutLinkEnabled(link, enabled) {
  if (!link) {
    return
  }

  const disabled = !enabled
  link.classList.toggle('button--disabled', disabled)
  link.classList.toggle('disabled', disabled)
  link.setAttribute('aria-disabled', disabled ? 'true' : 'false')

  if (disabled) {
    link.setAttribute('tabindex', '-1')
  } else {
    link.removeAttribute('tabindex')
  }
}

function formatPrice(value, currency, locale) {
  const numberLocale = locale === 'de' ? 'de-DE' : 'en-GB'

  try {
    const formatted = new Intl.NumberFormat(numberLocale, { style: 'currency', currency }).format(value)
    return `${formatted}${locale === 'de' ? ' netto' : ' net'}`
  } catch (_error) {
    return `${value} ${currency}${locale === 'de' ? ' netto' : ' net'}`
  }
}

function mountCalculator(calculator) {
  if (calculator.dataset.xmasPriceCalculatorInitialized === 'true') {
    return
  }

  const form = calculator.querySelector('[data-xmas-price-calculator-form]')
  const packageInputs = Array.from(calculator.querySelectorAll('[data-xmas-price-package]'))
  const areaInput = calculator.querySelector('[data-xmas-price-area]')
  const eventDaysInput = calculator.querySelector('[data-xmas-price-event-days]')
  const areaErrorNode = calculator.querySelector('[data-xmas-price-area-error]')
  const eventDaysErrorNode = calculator.querySelector('[data-xmas-price-event-days-error]')
  const priceNode = calculator.querySelector('[data-xmas-price-value]')
  const summaryNode = calculator.querySelector('[data-xmas-price-summary]')
  const noteNode = calculator.querySelector('[data-xmas-price-note]')
  const errorNode = calculator.querySelector('[data-xmas-price-error]')
  const checkoutLink = calculator.querySelector('[data-xmas-price-checkout-link]')
  const offerLink = calculator.querySelector('[data-xmas-price-offer-link]')
  const quoteLink = calculator.querySelector('[data-xmas-price-quote-link]')

  if (!form || !areaInput || !eventDaysInput || !areaErrorNode || !eventDaysErrorNode || !priceNode || !noteNode || !errorNode) {
    return
  }

  const previewUrl = calculator.dataset.xmasPricePreviewUrl
  const locale = calculator.dataset.xmasLocale || 'en'
  const initialMessage = calculator.dataset.xmasInitialMessage || ''
  const loadingMessage = calculator.dataset.xmasLoadingMessage || ''
  const errorMessage = calculator.dataset.xmasErrorMessage || ''
  const netNote = calculator.dataset.xmasNetNote || ''
  const quoteBaseUrl = quoteLink ? new URL(quoteLink.href, window.location.origin) : null
  const offerBaseUrl = offerLink ? new URL(offerLink.href, window.location.origin) : null
  const defaultPackageKey = calculator.dataset.xmasDefaultPackageKey || 'four_hour'
  let requestId = 0
  let lastTrackedCalculationKey = null
  let pendingPriceCalculation = null
  let pricingViewTracked = false
  let latestPriceSnapshot = null

  function selectedPackageKey() {
    return packageInputs.find((input) => input.checked)?.value || defaultPackageKey
  }

  function trackPricingView() {
    pricingViewTracked = trackChristmasMarketPricingView({ pricing_version: calculator.dataset.xmasPricingVersion }) || pricingViewTracked
  }

  function trackPriceCalculation(priceSnapshot) {
    const calculationKey = JSON.stringify(priceSnapshot)
    if (calculationKey === lastTrackedCalculationKey) {
      return
    }

    pendingPriceCalculation = { calculationKey, priceSnapshot }
    if (trackChristmasMarketPriceCalculated(priceSnapshot)) {
      lastTrackedCalculationKey = calculationKey
      pendingPriceCalculation = null
    }
  }

  function handleConsentAccepted() {
    trackPricingView()
    if (pendingPriceCalculation) {
      trackPriceCalculation(pendingPriceCalculation.priceSnapshot)
    }
  }

  trackPricingView()
  if (!pricingViewTracked) {
    document.addEventListener('termsfeed.consentAccepted', handleConsentAccepted, { once: true })
  }

  function clearFieldErrors() {
    setFieldError(areaInput, areaErrorNode, '')
    setFieldError(eventDaysInput, eventDaysErrorNode, '')
  }

  function localFieldErrors() {
    const errors = {}
    const area = String(areaInput.value || '').trim()
    const eventDays = String(eventDaysInput.value || '').trim()
    const areaValues = Array.from(areaInput.options).map((option) => option.value)
    const minDays = Number(eventDaysInput.min)
    const maxDays = Number(eventDaysInput.max)

    if (!area || !areaValues.includes(area)) {
      errors.area_sqm = calculator.dataset.xmasAreaError || ''
    }
    if (!/^\d+$/.test(eventDays) || Number(eventDays) < minDays || Number(eventDays) > maxDays) {
      errors.event_days = calculator.dataset.xmasEventDaysError || ''
    }

    return errors
  }

  function renderFieldErrors(fieldErrors) {
    setFieldError(areaInput, areaErrorNode, fieldErrors.area_sqm || '')
    setFieldError(eventDaysInput, eventDaysErrorNode, fieldErrors.event_days || '')
  }

  calculator.dataset.xmasPriceCalculatorInitialized = 'true'

  function resetResult() {
    clearFieldErrors()
    setHidden(priceNode, true)
    setHidden(summaryNode, true)
    setHidden(errorNode, true)
    setText(noteNode, initialMessage)
    latestPriceSnapshot = null
    setCheckoutLinkEnabled(checkoutLink, false)
    setCheckoutLinkEnabled(offerLink, false)
    updateQuoteLink()
  }

  function showLoading() {
    clearFieldErrors()
    setHidden(priceNode, true)
    setHidden(summaryNode, true)
    setHidden(errorNode, true)
    setText(noteNode, loadingMessage)
    latestPriceSnapshot = null
    setCheckoutLinkEnabled(checkoutLink, false)
    setCheckoutLinkEnabled(offerLink, false)
    updateQuoteLink()
  }

  function showError(message, fieldErrors = {}) {
    renderFieldErrors(fieldErrors)
    setHidden(priceNode, true)
    setHidden(summaryNode, true)
    setText(noteNode, '')
    setText(errorNode, message)
    setHidden(errorNode, !message)
    latestPriceSnapshot = null
    setCheckoutLinkEnabled(checkoutLink, false)
    setCheckoutLinkEnabled(offerLink, false)
    updateQuoteLink()
  }

  function updateQuoteLink(priceSnapshot = null) {
    if (!quoteLink || !quoteBaseUrl) {
      return
    }

    const quoteUrl = new URL(quoteBaseUrl.toString())
    quoteUrl.searchParams.set('xmas_quote', '1')
    quoteUrl.searchParams.set('xmas_source', 'christmas_market_calculator')
    if (priceSnapshot) {
      quoteUrl.searchParams.set('area_sqm', String(priceSnapshot.area_tier))
      quoteUrl.searchParams.set('event_days', String(priceSnapshot.event_days))
      quoteUrl.searchParams.set('package_key', String(priceSnapshot.package_key))
    } else {
      quoteUrl.searchParams.delete('area_sqm')
      quoteUrl.searchParams.delete('event_days')
      quoteUrl.searchParams.delete('package_key')
    }
    quoteLink.href = quoteUrl.toString()
  }

  function updateOfferLink(priceSnapshot = null) {
    if (!offerLink || !offerBaseUrl) {
      return
    }

    const offerUrl = new URL(offerBaseUrl.toString())
    if (priceSnapshot) {
      offerUrl.searchParams.set('area_sqm', String(priceSnapshot.area_tier))
      offerUrl.searchParams.set('event_days', String(priceSnapshot.event_days))
      offerUrl.searchParams.set('package_key', String(priceSnapshot.package_key))
    } else {
      offerUrl.searchParams.delete('area_sqm')
      offerUrl.searchParams.delete('event_days')
      offerUrl.searchParams.delete('package_key')
    }
    offerLink.href = offerUrl.toString()
  }

  async function refreshPrice() {
    const area = String(areaInput.value || '').trim()
    const eventDays = String(eventDaysInput.value || '').trim()
    const packageKey = selectedPackageKey()
    const currentRequestId = ++requestId

    if (!area || !eventDays) {
      resetResult()
      return
    }

    const fieldErrors = localFieldErrors()
    if (Object.keys(fieldErrors).length > 0) {
      showError('', fieldErrors)
      return
    }

    showLoading()

    try {
      const url = new URL(previewUrl, window.location.origin)
      url.searchParams.set('area_sqm', area)
      url.searchParams.set('event_days', eventDays)
      url.searchParams.set('package_key', packageKey)

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
      const payload = await response.json()

      if (currentRequestId !== requestId) {
        return
      }

      if (!response.ok || typeof payload.net_price !== 'number') {
        const error = new Error('xmas_price_preview_failed')
        error.userMessage = payload.message || errorMessage
        error.fieldErrors = payload.field_errors || {}
        throw error
      }

      clearFieldErrors()
      const priceSnapshot = {
        area_tier: payload.area_tier,
        event_days: payload.event_days,
        net_price: payload.net_price,
        package_key: payload.package_key || packageKey,
        pricing_version: payload.pricing_version
      }
      latestPriceSnapshot = priceSnapshot
      trackPriceCalculation(priceSnapshot)
      setText(priceNode, formatPrice(payload.net_price, payload.currency || 'EUR', locale))
      if (payload.package_summary) {
        setText(summaryNode, payload.package_summary)
      }
      setHidden(priceNode, false)
      setHidden(summaryNode, false)
      setHidden(errorNode, true)
      setText(noteNode, netNote)
      if (checkoutLink) {
        const checkoutUrl = new URL(calculator.dataset.xmasCheckoutUrl, window.location.origin)
        checkoutUrl.searchParams.set('area_sqm', String(priceSnapshot.area_tier))
        checkoutUrl.searchParams.set('event_days', String(priceSnapshot.event_days))
        checkoutUrl.searchParams.set('package_key', String(priceSnapshot.package_key))
        if (payload.package_name) {
          checkoutLink.textContent = locale === 'de' ? `${payload.package_name} buchen` : `Book ${payload.package_name}`
        }
        checkoutLink.href = checkoutUrl.toString()
      }
      updateQuoteLink(priceSnapshot)
      updateOfferLink(priceSnapshot)
      setCheckoutLinkEnabled(checkoutLink, true)
      setCheckoutLinkEnabled(offerLink, true)
    } catch (error) {
      if (currentRequestId !== requestId) {
        return
      }

      showError(error.userMessage || errorMessage, error.fieldErrors || {})
    }
  }

  form.addEventListener('submit', function (event) {
    event.preventDefault()
    refreshPrice()
  })
  areaInput.addEventListener('change', refreshPrice)
  eventDaysInput.addEventListener('change', refreshPrice)
  packageInputs.forEach((input) => input.addEventListener('change', refreshPrice))
  if (checkoutLink) {
    checkoutLink.addEventListener('click', function (event) {
      if (checkoutLink.getAttribute('aria-disabled') === 'true') {
        event.preventDefault()
        return
      }

      if (latestPriceSnapshot) {
        trackChristmasMarketCheckoutStarted(latestPriceSnapshot)
      }
    })
  }
  if (offerLink) {
    offerLink.addEventListener('click', function (event) {
      if (offerLink.getAttribute('aria-disabled') === 'true') {
        event.preventDefault()
      }
    })
  }

  resetResult()
}

function mountCheckoutPackagePicker(form) {
  if (form.dataset.xmasCheckoutPackagePickerInitialized === 'true') {
    return
  }

  const checkoutScope = form.closest('.row--large') || form
  const packageInputs = Array.from(form.querySelectorAll('[data-xmas-checkout-package]'))
  const licenseTermInputs = Array.from(form.querySelectorAll('input[name="license_term_years"]'))
  const priceNode = checkoutScope.querySelector('[data-xmas-checkout-price]')
  const packageNameNode = checkoutScope.querySelector('[data-xmas-checkout-package-name]')
  const packageHoursNode = checkoutScope.querySelector('[data-xmas-checkout-package-hours]')
  const packageSummaryNode = checkoutScope.querySelector('[data-xmas-checkout-package-summary]')
  const errorNode = checkoutScope.querySelector('[data-xmas-checkout-price-error]')
  const retryButton = checkoutScope.querySelector('[data-xmas-checkout-retry]')
  const submitButton = form.querySelector('[data-xmas-checkout-submit]')

  if (!packageInputs.length || !priceNode || !packageNameNode || !packageHoursNode || !packageSummaryNode || !errorNode || !retryButton || !submitButton) {
    return
  }

  const previewUrl = form.dataset.xmasPricePreviewUrl
  const locale = form.dataset.xmasLocale || 'en'
  const errorMessage = form.dataset.xmasCheckoutPriceError || ''
  let requestId = 0
  let updatePending = false
  form.dataset.xmasCheckoutPackagePickerInitialized = 'true'

  async function refreshPackagePrice() {
    const packageInput = packageInputs.find((input) => input.checked)
    if (!packageInput) {
      return
    }

    const selectedOption = packageInput.closest('[data-xmas-checkout-package-option]')
    const selectedName = selectedOption?.querySelector('[data-xmas-checkout-option-name]')?.textContent
    const selectedHours = selectedOption?.querySelector('[data-xmas-checkout-option-hours]')?.textContent
    const selectedSummary = selectedOption?.querySelector('[data-xmas-checkout-option-summary]')?.textContent
    if (selectedName) packageNameNode.textContent = selectedName
    if (selectedHours) packageHoursNode.textContent = selectedHours
    if (selectedSummary) packageSummaryNode.textContent = selectedSummary

    const currentRequestId = ++requestId
    const area = String(form.dataset.xmasCheckoutAreaSqm || '').trim()
    const eventDays = String(form.dataset.xmasCheckoutEventDays || '').trim()
    updatePending = true
    submitButton.disabled = true
    priceNode.setAttribute('aria-busy', 'true')
    priceNode.textContent = locale === 'de' ? 'Preis wird aktualisiert …' : 'Updating price…'
    errorNode.hidden = true
    errorNode.textContent = ''
    retryButton.hidden = true

    try {
      const url = new URL(previewUrl, window.location.origin)
      url.searchParams.set('area_sqm', area)
      url.searchParams.set('event_days', eventDays)
      url.searchParams.set('package_key', packageInput.value)
      url.searchParams.set('license_term_years', licenseTermInputs.find((input) => input.checked)?.value || '1')

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
      const payload = await response.json()

      if (currentRequestId !== requestId) {
        return
      }
      if (!response.ok || typeof payload.net_price !== 'number') {
        throw new Error(payload.message || errorMessage)
      }

      priceNode.textContent = formatPrice(payload.net_price, payload.currency || 'EUR', locale)
      packageNameNode.textContent = payload.package_name || packageInput.value
      packageHoursNode.textContent = locale === 'de'
        ? `Mindestens ${payload.playlist_hours} Stunden; jährliche Titelliste`
        : `At least ${payload.playlist_hours} hours; annual track list`
      submitButton.disabled = false
    } catch (_error) {
      if (currentRequestId !== requestId) {
        return
      }

      priceNode.textContent = '—'
      errorNode.textContent = errorMessage
      errorNode.hidden = false
      retryButton.hidden = false
    } finally {
      if (currentRequestId === requestId) {
        updatePending = false
        priceNode.setAttribute('aria-busy', 'false')
      }
    }
  }

  packageInputs.forEach((input) => {
    input.addEventListener('change', refreshPackagePrice)
  })
  licenseTermInputs.forEach((input) => {
    input.addEventListener('change', refreshPackagePrice)
  })
  retryButton.addEventListener('click', refreshPackagePrice)
  form.addEventListener('submit', (event) => {
    if (updatePending || !errorNode.hidden) {
      event.preventDefault()
    }
  })
}

export function initChristmasMarketPricing(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') {
    return
  }

  root.querySelectorAll('[data-xmas-price-calculator]').forEach(mountCalculator)
  root.querySelectorAll('[data-xmas-checkout-package-picker]').forEach(mountCheckoutPackagePicker)
}
